import React, { useState, useEffect, useRef } from 'react';
import { Calendar as CalendarIcon, Clock, MapPin, User, Plus, Bell, Edit, Trash2, Shield, Settings, LogOut, Lock, X, Users, Key, Database, Cloud, CloudOff, RefreshCw } from 'lucide-react';

export default function App() {
  // --- 1. การเชื่อมต่อฐานข้อมูล (GAS + Local Fallback) ---
  const [gasUrl, setGasUrl] = useState(() => localStorage.getItem('dtc_gas_url') || '');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // สถานะข้อมูล
  const [events, setEvents] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [users, setUsers] = useState([]);
  const [lineSettings, setLineSettings] = useState({ enabled: false, channelAccessToken: '', targetId: '' });

  // ฟังก์ชันโหลดข้อมูลตอนเปิดเว็บ
  useEffect(() => {
    const initData = async () => {
      setDataLoaded(false);
      if (!gasUrl) {
        // ถ้ายัไม่มี URL ให้โหลดจากเครื่อง (Local) เพื่อให้ทดสอบเว็บได้
        setEvents(JSON.parse(localStorage.getItem('dtc_events')) || []);
        setStaffList(JSON.parse(localStorage.getItem('dtc_staff')) || []);
        setUsers(JSON.parse(localStorage.getItem('dtc_users')) || [{ id: '1', username: 'admin', password: '1234', name: 'แอดมิน', role: 'admin' }, { id: '2', username: 'staff', password: '1234', name: 'เจ้าหน้าที่', role: 'staff' }]);
        setLineSettings(JSON.parse(localStorage.getItem('dtc_linesettings')) || { enabled: false, channelAccessToken: '', targetId: '' });
        setDataLoaded(true);
        return;
      }

      // ถ้ามี URL ให้โหลดจาก Google Sheets (Cloud)
      setIsSyncing(true);
      try {
        const res = await fetch(gasUrl, { method: 'POST', body: JSON.stringify({ action: 'getData' }) });
        const json = await res.json();
        setEvents(json.events || []);
        setUsers(json.users || []);
        setStaffList(json.staff || []);
        if (json.settings) {
          setLineSettings({
            enabled: String(json.settings.enabled).toLowerCase() === 'true',
            channelAccessToken: json.settings.channelAccessToken || '',
            targetId: json.settings.targetId || ''
          });
        }
      } catch (e) {
        alert('ไม่สามารถเชื่อมต่อฐานข้อมูล Google Sheets ได้ กรุณาตรวจสอบ URL');
        setGasUrl(''); // รีเซ็ต URL ถ้าพัง
      } finally {
        setIsSyncing(false);
        setDataLoaded(true);
      }
    };
    initData();
    localStorage.setItem('dtc_gas_url', gasUrl);
  }, [gasUrl]);

  // ฟังก์ชันเซฟข้อมูลขึ้น Cloud แบบ Background (ทำทันทีที่ข้อมูลใน React เปลี่ยนแปลง)
  const saveToCloud = async (action, data) => {
    if (!gasUrl) return;
    setIsSyncing(true);
    try {
      await fetch(gasUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ action, data })
      });
    } catch (err) {
      console.error("Cloud Sync Error:", err);
    } finally {
      setIsSyncing(false);
    }
  };

  // ตรวจจับการเปลี่ยนแปลงและ Sync
  useEffect(() => {
    if (!dataLoaded) return;
    if (gasUrl) saveToCloud('updateEvents', events);
    else localStorage.setItem('dtc_events', JSON.stringify(events));
  }, [events, dataLoaded, gasUrl]);

  useEffect(() => {
    if (!dataLoaded) return;
    if (gasUrl) saveToCloud('updateStaff', staffList);
    else localStorage.setItem('dtc_staff', JSON.stringify(staffList));
  }, [staffList, dataLoaded, gasUrl]);

  useEffect(() => {
    if (!dataLoaded) return;
    if (gasUrl) saveToCloud('updateUsers', users);
    else localStorage.setItem('dtc_users', JSON.stringify(users));
  }, [users, dataLoaded, gasUrl]);

  useEffect(() => {
    if (!dataLoaded) return;
    if (gasUrl) saveToCloud('updateSettings', lineSettings);
    else localStorage.setItem('dtc_linesettings', JSON.stringify(lineSettings));
  }, [lineSettings, dataLoaded, gasUrl]);


  // --- 2. สถานะผู้ใช้และ UI ---
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ username: '', password: '', name: '', role: 'staff' });
  const [userFormError, setUserFormError] = useState('');
  const [editingUserId, setEditingUserId] = useState(null);

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [lineTestStatus, setLineTestStatus] = useState({ type: '', message: '' });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', date: '', startTime: '', endTime: '', location: '', responsible: '', description: '' });

  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [newStaffName, setNewStaffName] = useState('');

  const canManageUsers = currentUser?.role === 'admin';

  // --- 3. ฟังก์ชัน Helpers ---
  const getDaysInMonth = () => new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const formatDateToYYYYMMDD = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const formatThaiDate = (dateString) => {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '';
    const months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear() + 543}`;
  };

  const getEventStatus = (dateStr, endTimeStr) => {
    const today = new Date();
    const eventDate = new Date(dateStr);
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let isPastTime = false;
    if (diffDays === 0 && endTimeStr) {
      const now = new Date();
      const [endH, endM] = endTimeStr.split(':').map(Number);
      const endTime = new Date();
      endTime.setHours(endH, endM, 0, 0);
      if (now > endTime) isPastTime = true;
    }

    if (diffDays < 0 || isPastTime) return { bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200', isPast: true };
    if (diffDays === 0) return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', isToday: true };
    if (diffDays <= 3) return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', isSoon: true };
    return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', isFuture: true };
  };

  // --- 4. ฟังก์ชันจัดการข้อมูล ---
  const handleLogin = (e) => {
    e.preventDefault();
    const user = users.find(u => u.username === loginForm.username && u.password === loginForm.password);
    if (user) {
      setIsLoggedIn(true); setCurrentUser(user); setIsLoginModalOpen(false); setLoginForm({ username: '', password: '' }); setLoginError('');
    } else { setLoginError('ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง'); }
  };
  const handleLogout = () => { setIsLoggedIn(false); setCurrentUser(null); };

  const handleSaveUser = (e) => {
    e.preventDefault();
    if (!newUserForm.username || !newUserForm.name || (!newUserForm.password && !editingUserId)) { setUserFormError('กรอกข้อมูลให้ครบถ้วน'); return; }
    if (editingUserId) setUsers(users.map(u => u.id === editingUserId ? { ...u, ...newUserForm, ...(newUserForm.password ? {} : { password: u.password }) } : u));
    else setUsers([...users, { ...newUserForm, id: Date.now().toString() }]);
    setNewUserForm({ username: '', password: '', name: '', role: 'staff' }); setEditingUserId(null); setUserFormError('');
  };

  const handleTestLineNotify = async () => {
    if (!lineSettings.channelAccessToken || !lineSettings.targetId) return setLineTestStatus({ type: 'error', message: 'กรุณากรอกข้อมูลให้ครบ' });
    setLineTestStatus({ type: 'info', message: 'กำลังส่งข้อความ...' });

    if (gasUrl) {
      // ส่งผ่าน Google Apps Script (ยิง API แทนเรา)
      try {
        const res = await fetch(gasUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify({ action: 'testLine', settings: lineSettings, message: '\n🔔 ทดสอบการแจ้งเตือนจากฐานข้อมูลระบบ' })
        });
        const data = await res.json();
        if (data.success) setLineTestStatus({ type: 'success', message: 'สำเร็จ!' });
        else setLineTestStatus({ type: 'error', message: data.error || 'ตั้งค่าผิดพลาด' });
      } catch (e) { setLineTestStatus({ type: 'error', message: 'เชื่อมต่อ Google Sheets ล้มเหลว' }); }
    } else {
      setLineTestStatus({ type: 'error', message: 'ต้องใส่ URL ฐานข้อมูลก่อนจึงจะทดสอบได้' });
    }
  };

  const handleSubmitEvent = (e) => {
    e.preventDefault();
    if (editingId) setEvents(events.map(ev => ev.id === editingId ? { ...formData, id: editingId, lineNotified: false } : ev));
    else setEvents([...events, { ...formData, id: Date.now().toString(), lineNotified: false }]);
    setIsModalOpen(false);
  };

  if (!dataLoaded) return <div className="min-h-screen flex items-center justify-center bg-slate-100 font-bold text-slate-500 animate-pulse">กำลังโหลดข้อมูลฐานข้อมูล...</div>;

  const upcomingEvents = [...events].sort((a, b) => new Date(a.date + 'T' + a.startTime) - new Date(b.date + 'T' + b.startTime));

  return (
    <div className="min-h-screen bg-slate-100 font-sans pb-10">
      {/* --- Header --- */}
      <header className="bg-indigo-700 shadow-md border-b border-indigo-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col md:flex-row justify-between items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-white p-2 rounded-lg"><CalendarIcon size={24} className="text-indigo-700" /></div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white tracking-tight flex items-center gap-2">
                ระบบปฏิทินงาน
                {/* สถานะ Cloud */}
                <span className="flex items-center gap-1 text-[10px] md:text-xs font-medium bg-white/20 px-2.5 py-1 rounded-full text-indigo-100">
                  {gasUrl ? (isSyncing ? <><RefreshCw size={12} className="animate-spin" /> Syncing</> : <><Cloud size={12} /> Cloud</>) : <><CloudOff size={12} /> Local</>}
                </span>
              </h1>
              <p className="text-indigo-200 text-xs md:text-sm">ศูนย์เทคโนโลยีดิจิทัล</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isLoggedIn ? (
              <div className="flex items-center gap-2">
                {canManageUsers && (
                  <>
                    <button onClick={() => setIsUserModalOpen(true)} className="p-2 text-indigo-100 hover:bg-indigo-600 rounded-lg" title="จัดการผู้ใช้"><Shield size={20} /></button>
                    <button onClick={() => setIsSettingsModalOpen(true)} className="p-2 text-indigo-100 hover:bg-indigo-600 rounded-lg" title="ตั้งค่าระบบ"><Settings size={20} /></button>
                  </>
                )}
                <div className="flex items-center gap-3 bg-indigo-800/60 px-4 py-1.5 rounded-full border border-indigo-500/30">
                  <span className="text-sm font-semibold text-white">{currentUser.name}</span>
                  <button onClick={handleLogout} className="p-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-full"><LogOut size={16} /></button>
                </div>
              </div>
            ) : (
              <button onClick={() => setIsLoginModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-white text-indigo-700 rounded-lg font-medium shadow-sm"><Lock size={16} />เข้าสู่ระบบ</button>
            )}
          </div>
        </div>
      </header>

      {/* --- Main Layout --- */}
      <main className="max-w-7xl mx-auto px-4 py-6 flex flex-col lg:flex-row gap-6">
        
        {/* Calendar Grid */}
        <div className="w-full lg:w-2/3 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 bg-slate-50 border-b flex justify-between items-center font-bold text-slate-800">
            <div className="flex items-center gap-2"><CalendarIcon size={20} className="text-indigo-600"/> ปฏิทินเดือนนี้</div>
            <div className="text-sm font-medium bg-white px-3 py-1 rounded shadow-sm border">{formatThaiDate(currentDate)}</div>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2 text-center text-xs md:text-sm font-bold text-slate-500">
              {['อา','จ','อ','พ','พฤ','ศ','ส'].map(d => <div key={d} className="bg-slate-50 py-2 rounded">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1 md:gap-2">
              {Array.from({ length: getDaysInMonth() }).map((_, i) => {
                const date = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
                const isToday = date === formatDateToYYYYMMDD(new Date());
                const dayEvents = events.filter(e => e.date === date);
                return (
                  <div key={i} className={`min-h-[80px] md:min-h-[100px] border rounded-lg p-1 ${isToday ? 'bg-indigo-50 border-indigo-200' : 'bg-white'}`}>
                    <div className={`text-right text-xs md:text-sm font-medium pr-1 ${isToday ? 'text-indigo-700' : 'text-slate-600'}`}>{i + 1}</div>
                    <div className="mt-1 space-y-1">
                      {dayEvents.map(e => {
                        const status = getEventStatus(e.date, e.endTime);
                        return <div key={e.id} className={`text-[9px] md:text-[10px] p-1 rounded truncate border ${status.bg} ${status.text} ${status.border}`}><span className="font-bold">{e.startTime}</span> {e.title}</div>
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-full lg:w-1/3 flex flex-col gap-6">
          {isLoggedIn && (
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <button onClick={() => { setFormData({title:'', date:formatDateToYYYYMMDD(new Date()), startTime:'', endTime:'', location:'', responsible:'', description:''}); setEditingId(null); setIsModalOpen(true); }} className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"><Plus size={18}/> เพิ่มงานลงปฏิทิน</button>
              {canManageUsers && <button onClick={() => setIsStaffModalOpen(true)} className="w-full mt-3 py-2.5 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-lg font-medium flex items-center justify-center gap-2 border transition-colors"><Users size={18}/> บุคลากร ({staffList.length})</button>}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border flex-1 flex flex-col max-h-[600px]">
            <div className="p-4 bg-slate-50 border-b flex justify-between font-bold text-slate-800"><span className="flex items-center gap-2"><Bell size={20} className="text-amber-500"/> รายการงานทั้งหมด</span><span className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full">{upcomingEvents.length}</span></div>
            <div className="p-4 overflow-y-auto space-y-3">
              {upcomingEvents.length === 0 && <div className="text-center text-slate-400 text-sm py-10">ยังไม่มีรายการงาน</div>}
              {upcomingEvents.map(e => {
                const status = getEventStatus(e.date, e.endTime);
                return (
                  <div key={e.id} className={`p-3 rounded-lg border relative group transition-colors ${status.bg} ${status.border}`}>
                    <div className="flex justify-between items-start">
                      <h3 className={`font-bold text-sm ${status.text} ${status.isPast?'line-through opacity-60':''}`}>{e.title}</h3>
                      <span className="text-[10px] bg-white/60 px-2 py-0.5 rounded-full font-medium">{formatThaiDate(e.date)}</span>
                    </div>
                    <div className={`mt-2 text-xs space-y-1.5 ${status.text} opacity-80`}>
                      <div className="flex items-center"><Clock size={12} className="mr-1.5"/> {e.startTime} - {e.endTime || '?'} น.</div>
                      {e.location && <div className="flex items-center"><MapPin size={12} className="mr-1.5"/> {e.location}</div>}
                      {e.responsible && <div className="flex items-center"><User size={12} className="mr-1.5"/> {e.responsible}</div>}
                    </div>
                    {isLoggedIn && !status.isPast && (
                      <div className="absolute right-2 bottom-2 opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                        <button onClick={() => { setFormData(e); setEditingId(e.id); setIsModalOpen(true); }} className="p-1.5 bg-white text-indigo-600 rounded border shadow-sm hover:bg-indigo-50"><Edit size={12}/></button>
                        <button onClick={() => { if(window.confirm('ลบงานนี้?')) setEvents(events.filter(ev => ev.id !== e.id)); }} className="p-1.5 bg-white text-rose-600 rounded border shadow-sm hover:bg-rose-50"><Trash2 size={12}/></button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </main>

      {/* --- Modals --- */}
      
      {/* Login */}
      {isLoginModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-sm overflow-hidden shadow-2xl">
            <div className="p-6 text-center bg-slate-50 border-b">
              <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3"><Lock size={32}/></div>
              <h3 className="text-xl font-bold text-slate-800">เข้าสู่ระบบ</h3>
            </div>
            <form onSubmit={handleLogin} className="p-6 space-y-4">
              {loginError && <div className="text-sm text-rose-600 bg-rose-50 p-2 rounded text-center border border-rose-100">{loginError}</div>}
              <input type="text" placeholder="Username (เช่น admin หรือ staff)" required className="w-full p-2.5 border rounded-lg bg-slate-50 focus:bg-white" value={loginForm.username} onChange={e=>setLoginForm({...loginForm, username: e.target.value})} />
              <input type="password" placeholder="Password (ค่าเริ่มต้น 1234)" required className="w-full p-2.5 border rounded-lg bg-slate-50 focus:bg-white" value={loginForm.password} onChange={e=>setLoginForm({...loginForm, password: e.target.value})} />
              <div className="pt-2 flex gap-2">
                <button type="submit" className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold transition-colors">Login</button>
                <button type="button" onClick={()=>setIsLoginModalOpen(false)} className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg font-bold transition-colors">ยกเลิก</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Event Form */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center bg-slate-50"><h3 className="font-bold text-slate-800">{editingId ? 'แก้ไขงาน' : 'เพิ่มงานใหม่'}</h3><button onClick={()=>setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X/></button></div>
            <form onSubmit={handleSubmitEvent} className="p-5 space-y-4">
              <div><label className="text-sm font-bold text-slate-700 mb-1 block">ชื่องาน <span className="text-rose-500">*</span></label><input type="text" required className="w-full p-2 border rounded bg-slate-50 focus:bg-white" value={formData.title} onChange={e=>setFormData({...formData, title: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm font-bold text-slate-700 mb-1 block">วันที่ <span className="text-rose-500">*</span></label><input type="date" required className="w-full p-2 border rounded bg-slate-50 focus:bg-white" value={formData.date} onChange={e=>setFormData({...formData, date: e.target.value})} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-sm font-bold text-slate-700 mb-1 block">เริ่ม <span className="text-rose-500">*</span></label><input type="time" required className="w-full p-2 border rounded bg-slate-50 focus:bg-white" value={formData.startTime} onChange={e=>setFormData({...formData, startTime: e.target.value})} /></div>
                  <div><label className="text-sm font-bold text-slate-700 mb-1 block">สิ้นสุด</label><input type="time" className="w-full p-2 border rounded bg-slate-50 focus:bg-white" value={formData.endTime} onChange={e=>setFormData({...formData, endTime: e.target.value})} /></div>
                </div>
              </div>
              <div><label className="text-sm font-bold text-slate-700 mb-1 block">สถานที่</label><input type="text" className="w-full p-2 border rounded bg-slate-50 focus:bg-white" value={formData.location} onChange={e=>setFormData({...formData, location: e.target.value})} /></div>
              <div>
                <label className="text-sm font-bold text-slate-700 mb-1 block">ผู้รับผิดชอบ</label>
                <select className="w-full p-2 border rounded bg-slate-50 focus:bg-white" value={formData.responsible} onChange={e=>setFormData({...formData, responsible: e.target.value})}>
                  <option value="">-- เลือกจากรายชื่อบุคลากร --</option>
                  {staffList.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
              </div>
              <button type="submit" className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold mt-2 transition-colors">บันทึกข้อมูล</button>
            </form>
          </div>
        </div>
      )}

      {/* Users Management */}
      {isUserModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex justify-between bg-slate-50"><h3 className="font-bold flex items-center gap-2 text-slate-800"><Shield className="text-indigo-600"/> จัดการผู้ใช้งานระบบ</h3><button onClick={()=>setIsUserModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X/></button></div>
            <div className="p-5 flex flex-col md:flex-row gap-6 overflow-y-auto">
              <form onSubmit={handleSaveUser} className="w-full md:w-1/3 space-y-3 md:border-r md:pr-5">
                <h4 className="font-bold text-sm text-slate-800 mb-2">{editingUserId ? 'แก้ไขข้อมูล' : 'เพิ่มผู้ใช้ใหม่'}</h4>
                {userFormError && <div className="text-xs text-rose-600 bg-rose-50 p-2 rounded border border-rose-100">{userFormError}</div>}
                <input type="text" placeholder="ชื่อ-สกุล" className="w-full p-2 border rounded text-sm bg-slate-50 focus:bg-white" value={newUserForm.name} onChange={e=>setNewUserForm({...newUserForm, name: e.target.value})} />
                <input type="text" placeholder="Username" className="w-full p-2 border rounded text-sm bg-slate-50 focus:bg-white" value={newUserForm.username} onChange={e=>setNewUserForm({...newUserForm, username: e.target.value})} />
                <input type="password" placeholder={editingUserId ? "รหัสผ่านใหม่ (เว้นว่างได้)" : "รหัสผ่าน *"} className="w-full p-2 border rounded text-sm bg-slate-50 focus:bg-white" value={newUserForm.password} onChange={e=>setNewUserForm({...newUserForm, password: e.target.value})} />
                <select className="w-full p-2 border rounded text-sm bg-slate-50 focus:bg-white" value={newUserForm.role} onChange={e=>setNewUserForm({...newUserForm, role: e.target.value})}>
                  <option value="staff">Staff (เจ้าหน้าที่)</option><option value="admin">Admin (ผู้ดูแลระบบ)</option>
                </select>
                <div className="flex gap-2 pt-2">
                  <button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded text-sm font-bold transition-colors">บันทึก</button>
                  {editingUserId && <button type="button" onClick={()=>{setEditingUserId(null); setNewUserForm({username:'',password:'',name:'',role:'staff'})}} className="flex-1 bg-slate-100 hover:bg-slate-200 p-2 rounded text-sm font-bold transition-colors">ยกเลิก</button>}
                </div>
              </form>
              <div className="w-full md:w-2/3 space-y-2">
                <h4 className="font-bold text-sm text-slate-800 mb-2">รายชื่อในระบบ</h4>
                {users.map(u => (
                  <div key={u.id} className="flex justify-between items-center p-3 border rounded-lg bg-white shadow-sm hover:border-indigo-200 transition-colors">
                    <div><p className="text-sm font-bold text-slate-800">{u.name} <span className={`text-[10px] px-1.5 py-0.5 rounded ml-1 ${u.role === 'admin' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'}`}>{u.role}</span></p><p className="text-xs text-slate-500 mt-0.5">@{u.username}</p></div>
                    <div className="flex gap-1.5">
                      <button onClick={()=>{ setUsers(users.map(x=>x.id===u.id ? {...x, password:'1234'}:x)); alert(`รีเซ็ตรหัสผ่านของ ${u.name} เป็น 1234 แล้ว`); }} className="p-1.5 text-amber-600 bg-amber-50 hover:bg-amber-100 rounded border border-amber-100" title="รีเซ็ตรหัสเป็น 1234"><Key size={14}/></button>
                      <button onClick={()=>{setEditingUserId(u.id); setNewUserForm({...u, password:''})}} className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded border border-blue-100"><Edit size={14}/></button>
                      <button onClick={()=>{ if(u.id!==currentUser.id && window.confirm('ลบผู้ใช้นี้?')) setUsers(users.filter(x=>x.id!==u.id)) }} className={`p-1.5 rounded border ${u.id===currentUser.id?'text-slate-300 bg-slate-50 border-slate-100':'text-rose-600 bg-rose-50 hover:bg-rose-100 border-rose-100'}`} disabled={u.id===currentUser.id}><Trash2 size={14}/></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings / Database / Line */}
      {isSettingsModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="p-4 border-b flex justify-between bg-slate-50"><h3 className="font-bold flex items-center gap-2 text-slate-800"><Settings className="text-indigo-600"/> ตั้งค่าระบบ & ฐานข้อมูล</h3><button onClick={()=>setIsSettingsModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X/></button></div>
            <div className="p-5 space-y-6">
              
              {/* ส่วนฐานข้อมูล GAS */}
              <div className="space-y-3 pb-5 border-b">
                <label className="font-bold text-sm text-slate-800 flex items-center gap-2"><Database size={16} className="text-indigo-600"/> ฐานข้อมูล (Google Sheets)</label>
                <p className="text-[11px] text-slate-500 bg-blue-50 p-2 rounded border border-blue-100 leading-relaxed">วาง URL ของ Google Apps Script (Web App) เพื่อเปลี่ยนให้ระบบบันทึกข้อมูลออนไลน์อัตโนมัติ หากปล่อยว่างไว้ระบบจะบันทึกในเครื่องคุณเท่านั้น</p>
                <input type="text" placeholder="https://script.google.com/macros/s/..." className="w-full p-2.5 border rounded-lg text-sm bg-slate-50 focus:bg-white text-slate-600" value={gasUrl} onChange={e=>setGasUrl(e.target.value)} />
              </div>

              {/* ส่วน LINE */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 font-bold text-sm text-slate-800"><input type="checkbox" checked={lineSettings.enabled} onChange={e=>setLineSettings({...lineSettings, enabled: e.target.checked})} className="w-4 h-4 accent-indigo-600"/> เปิดใช้งาน LINE Messaging API</label>
                <div className={`space-y-3 transition-opacity ${lineSettings.enabled ? 'opacity-100':'opacity-50 pointer-events-none'}`}>
                  <input type="text" placeholder="Channel Access Token (Long-lived)" className="w-full p-2.5 border rounded-lg text-sm bg-slate-50 focus:bg-white" value={lineSettings.channelAccessToken} onChange={e=>setLineSettings({...lineSettings, channelAccessToken: e.target.value})} />
                  <input type="text" placeholder="Target ID (User ID U... หรือ Group ID C...)" className="w-full p-2.5 border rounded-lg text-sm bg-slate-50 focus:bg-white" value={lineSettings.targetId} onChange={e=>setLineSettings({...lineSettings, targetId: e.target.value})} />
                  {lineTestStatus.message && <div className={`text-xs p-2.5 rounded-lg border font-medium ${lineTestStatus.type==='success'?'bg-emerald-50 text-emerald-700 border-emerald-200':'bg-rose-50 text-rose-700 border-rose-200'}`}>{lineTestStatus.message}</div>}
                  <button onClick={handleTestLineNotify} className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm rounded-lg border transition-colors flex justify-center items-center gap-2"><Bell size={14}/> ทดสอบส่งข้อความ</button>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Staff Management */}
      {isStaffModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="p-4 border-b flex justify-between bg-slate-50"><h3 className="font-bold flex items-center gap-2 text-slate-800"><Users className="text-indigo-600"/> จัดการบุคลากร</h3><button onClick={()=>setIsStaffModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X/></button></div>
            <div className="p-5">
              <form onSubmit={e=>{e.preventDefault(); if(newStaffName.trim()) setStaffList([...staffList, {id: Date.now(), name: newStaffName.trim()}]); setNewStaffName('');}} className="flex gap-2 mb-4">
                <input type="text" placeholder="พิมพ์ชื่อ-สกุล..." className="flex-1 p-2.5 border rounded-lg text-sm bg-slate-50 focus:bg-white" value={newStaffName} onChange={e=>setNewStaffName(e.target.value)} />
                <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 rounded-lg text-sm font-bold transition-colors">เพิ่ม</button>
              </form>
              <div className="max-h-[300px] overflow-y-auto border rounded-lg divide-y bg-slate-50">
                {staffList.length === 0 && <div className="p-4 text-center text-sm text-slate-400">ยังไม่มีรายชื่อ</div>}
                {staffList.map(s => (
                  <div key={s.id} className="p-3 flex justify-between items-center text-sm bg-white hover:bg-slate-50">
                    <span className="font-medium text-slate-700">{s.name}</span>
                    <button onClick={()=>setStaffList(staffList.filter(x=>x.id!==s.id))} className="text-rose-500 p-1.5 hover:bg-rose-50 rounded"><Trash2 size={14}/></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}